/**
 * 技能商店管理页面 (Admin)
 */
import { useState, useEffect, useRef } from 'react';
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar';
import { AppSidebar } from '@/components/AppSidebar';
import { SiteHeader } from '@/components/SiteHeder';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Plus,
  Edit,
  Trash2,
  Upload,
  RefreshCw,
  Search,
  Sparkles,
  ShieldCheck,
  CheckCircle2,
  XCircle,
  FileCode2,
  Pin,
  PinOff,
} from 'lucide-react';
import { toast } from 'sonner';
import { marketApi, type SkillItem } from '@/services/marketApi';

const TOOL_GROUPS_OPTIONS = [
  { id: 'ring', label: '智能戒指体征 (ring)', sensitive: true },
  { id: 'mijia', label: '米家全屋智能 (mijia)', sensitive: true },
  { id: 'scheduler', label: '定时提醒任务 (scheduler)', sensitive: true },
  { id: 'mcp', label: '外部 MCP 工具 (mcp)', sensitive: true },
  { id: 'weather', label: '天气查询 (weather)', sensitive: false },
  { id: 'chat_history', label: '聊天记录搜索 (chat_history)', sensitive: false },
  { id: 'memory', label: '长期记忆存取 (memory)', sensitive: false },
  { id: 'rag', label: '知识库检索 (rag)', sensitive: false },
  { id: 'core', label: '基础核心能力 (core)', sensitive: false },
];

export function SkillManagementPage() {
  const [loading, setLoading] = useState(false);
  const [skills, setSkills] = useState<SkillItem[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [status, setStatus] = useState('all');

  // 编辑 / 新建弹窗
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState<Partial<SkillItem> | null>(null);
  const [isCreate, setIsCreate] = useState(false);
  // 记录编辑前的原始数据，用于判断 SKILL.md 是否被手工改动
  const originalSkillRef = useRef<SkillItem | null>(null);

  // 导入 SKILL.md 弹窗
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importOverrideId, setImportOverrideId] = useState('');
  const [importing, setImporting] = useState(false);

  const loadSkills = async () => {
    setLoading(true);
    try {
      const res = await marketApi.getAdminSkills({
        page,
        page_size: pageSize,
        search: search || undefined,
        category: category !== 'all' ? category : undefined,
        status: status !== 'all' ? status : undefined,
      });
      if (res.success && res.data) {
        setSkills(res.data.items || []);
        setTotal(res.data.total || 0);
      } else {
        toast.error(res.message || '加载技能列表失败');
      }
    } catch (err: any) {
      toast.error(err.message || '加载技能列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSkills();
  }, [page, category, status]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    loadSkills();
  };

  const handleOpenCreate = () => {
    setIsCreate(true);
    setEditingSkill({
      id: '',
      name: '',
      description: '',
      category: 'efficiency',
      version: '1.0.0',
      version_code: 1,
      always: false,
      access_level: 'free',
      tool_groups: [],
      content_markdown: '---\nid: my_skill\nname: "我的技能"\ndescription: "技能描述"\n---\n\n# 角色设定\n你是一个专业助手...',
      is_official: true,
      status: 'published',
      sort_order: 0,
    });
    setEditDialogOpen(true);
  };

  const handleOpenEdit = (item: SkillItem) => {
    setIsCreate(false);
    setEditingSkill({ ...item });
    originalSkillRef.current = item;
    setEditDialogOpen(true);
  };

  const handleSaveSkill = async () => {
    if (!editingSkill?.id || !editingSkill?.name) {
      toast.error('技能 ID 和名称不能为空');
      return;
    }
    try {
      if (isCreate) {
        const res = await marketApi.createSkill(editingSkill);
        if (res.success) {
          toast.success('创建技能成功');
          setEditDialogOpen(false);
          loadSkills();
        } else {
          toast.error(res.message || '创建失败');
        }
      } else {
        // SKILL.md 未手工改动时不回传该字段，
        // 后端据此用表单里的元数据同步重写 frontmatter，使修改对已安装客户端生效
        const original = originalSkillRef.current;
        const payload: Partial<SkillItem> =
          original && editingSkill.content_markdown === original.content_markdown
            ? { ...editingSkill, content_markdown: undefined }
            : editingSkill;
        const res = await marketApi.updateSkill(editingSkill.id, payload);
        if (res.success) {
          toast.success('更新技能成功');
          setEditDialogOpen(false);
          loadSkills();
        } else {
          toast.error(res.message || '更新失败');
        }
      }
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  };

  const handleDeleteSkill = async (id: string) => {
    try {
      const res = await marketApi.deleteSkill(id);
      if (res.success) {
        toast.success('删除成功');
        loadSkills();
      } else {
        toast.error(res.message || '删除失败');
      }
    } catch (err: any) {
      toast.error(err.message || '删除失败');
    }
  };

  const handleToggleStatus = async (item: SkillItem) => {
    const nextStatus = item.status === 'published' ? 'draft' : 'published';
    try {
      const res = await marketApi.updateSkillStatus(item.id, nextStatus);
      if (res.success) {
        toast.success(`已${nextStatus === 'published' ? '上架' : '下架'}`);
        loadSkills();
      } else {
        toast.error(res.message || '操作失败');
      }
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  };

  // 置顶 / 取消置顶：置顶技能在商店列表中排最前
  const handleTogglePin = async (item: SkillItem) => {
    try {
      const res = await marketApi.updateSkillPin(item.id, !item.pinned);
      if (res.success) {
        toast.success(res.data?.pinned ? '已置顶，商店列表中将排最前' : '已取消置顶');
        loadSkills();
      } else {
        toast.error(res.message || '操作失败');
      }
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  };

  // 投稿审核：通过上架 / 驳回
  const handleReview = async (item: SkillItem, approve: boolean) => {
    try {
      const res = await marketApi.updateSkillStatus(item.id, approve ? 'published' : 'rejected');
      if (res.success) {
        toast.success(approve ? '已通过并上架' : '已驳回');
        loadSkills();
      } else {
        toast.error(res.message || '操作失败');
      }
    } catch (err: any) {
      toast.error(err.message || '操作失败');
    }
  };

  const handleImportMarkdown = async () => {
    if (!importFile) {
      toast.error('请先选择 SKILL.md 文件');
      return;
    }
    setImporting(true);
    try {
      const res = await marketApi.importSkillMarkdown(importFile, importOverrideId || undefined);
      if (res.success) {
        toast.success('导入技能成功');
        setImportDialogOpen(false);
        setImportFile(null);
        setImportOverrideId('');
        loadSkills();
      } else {
        toast.error(res.message || '导入失败');
      }
    } catch (err: any) {
      toast.error(err.message || '导入失败');
    } finally {
      setImporting(false);
    }
  };

  const toggleToolGroup = (tgId: string) => {
    if (!editingSkill) return;
    const current = editingSkill.tool_groups || [];
    const exists = current.includes(tgId);
    const updated = exists ? current.filter((x) => x !== tgId) : [...current, tgId];
    setEditingSkill({ ...editingSkill, tool_groups: updated });
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <SiteHeader title="技能商店管理" />
        <div className="flex flex-1 flex-col gap-4 p-6">
          {/* 页面标题栏 */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Sparkles className="w-6 h-6 text-purple-600" />
                技能商店管理 (Skill Store)
              </h1>
              <p className="text-sm text-muted-foreground mt-1">
                管理与上架 Alita AI 智能体的技能与 Prompt 工作流，支持 SKILL.md 解析导入与敏感权限配置
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
                <Upload className="w-4 h-4 mr-2" />
                导入 SKILL.md
              </Button>
              <Button onClick={handleOpenCreate}>
                <Plus className="w-4 h-4 mr-2" />
                新建技能
              </Button>
            </div>
          </div>

          {/* 搜索过滤栏 */}
          <Card>
            <CardContent className="pt-6">
              <form onSubmit={handleSearchSubmit} className="flex flex-col md:flex-row gap-4">
                <div className="flex-1 flex items-center gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      placeholder="搜索技能 ID、名称、描述..."
                      value={search}
                      onChange={(e) => setSearch(e.target.value)}
                      className="pl-9"
                    />
                  </div>
                  <Button type="submit" variant="secondary">搜索</Button>
                </div>
                <div className="flex items-center gap-3">
                  <Select value={category} onValueChange={setCategory}>
                    <SelectTrigger className="w-[140px]">
                      <SelectValue placeholder="分类" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">全部分类</SelectItem>
                      <SelectItem value="health">健康生活</SelectItem>
                      <SelectItem value="efficiency">工作学习</SelectItem>
                      <SelectItem value="roleplay">角色扮演</SelectItem>
                      <SelectItem value="smart_home">智能家居</SelectItem>
                      <SelectItem value="tool">实用工具</SelectItem>
                      <SelectItem value="custom">自定义</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-[120px]">
                      <SelectValue placeholder="状态" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">全部状态</SelectItem>
                      <SelectItem value="published">已发布</SelectItem>
                      <SelectItem value="draft">草稿</SelectItem>
                      <SelectItem value="pending_review">待审核</SelectItem>
                      <SelectItem value="rejected">已驳回</SelectItem>
                      <SelectItem value="archived">已归档</SelectItem>
                    </SelectContent>
                  </Select>

                  <Button variant="ghost" size="icon" onClick={() => loadSkills()} title="刷新">
                    <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>

          {/* 技能列表表格 */}
          <Card>
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>技能列表</CardTitle>
                  <CardDescription>共 {total} 个技能</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>技能名称 / ID</TableHead>
                      <TableHead>分类</TableHead>
                      <TableHead>版本</TableHead>
                      <TableHead>权限/常驻</TableHead>
                      <TableHead>依赖工具组</TableHead>
                      <TableHead>好用度与评分</TableHead>
                      <TableHead>状态</TableHead>
                      <TableHead>下载量</TableHead>
                      <TableHead className="text-right">操作</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {skills.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={9} className="text-center py-12 text-muted-foreground">
                          {loading ? '加载中...' : '暂无技能数据'}
                        </TableCell>
                      </TableRow>
                    ) : (
                      skills.map((item) => (
                        <TableRow key={item.id}>
                          <TableCell>
                            <div>
                              <div
                                className="font-semibold flex items-center gap-1.5 cursor-pointer hover:text-blue-600 transition-colors"
                                onClick={() => handleOpenEdit(item)}
                                title="点击查看并编辑技能详情"
                              >
                                <span>{item.name}</span>
                                {item.is_official && (
                                  <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700 border-blue-200">
                                    官方
                                  </Badge>
                                )}
                                {item.pinned && (
                                  <Badge className="text-[10px] bg-amber-500 text-white px-1.5 py-0 gap-0.5">
                                    <Pin className="w-2.5 h-2.5" />
                                    置顶
                                  </Badge>
                                )}
                              </div>
                              <div className="text-xs text-muted-foreground font-mono mt-0.5">{item.id}</div>
                              {item.description && (
                                <div className="text-xs text-muted-foreground mt-1 line-clamp-1 max-w-[320px]">
                                  {item.description}
                                </div>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="secondary">{item.category}</Badge>
                          </TableCell>
                          <TableCell>
                            <span className="text-xs font-mono font-medium">v{item.version}</span>
                            <span className="text-[11px] text-muted-foreground ml-1">({item.version_code})</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1 items-start">
                              {item.access_level === 'vip' ? (
                                <Badge className="bg-amber-500 hover:bg-amber-600 text-white text-[11px]">VIP 专享</Badge>
                              ) : (
                                <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50 text-[11px]">免费</Badge>
                              )}
                              {item.always && (
                                <Badge variant="secondary" className="text-[10px]">常驻Prompt</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1 max-w-[200px]">
                              {item.tool_groups && item.tool_groups.length > 0 ? (
                                item.tool_groups.map((tg) => {
                                  const isSensitive = ['ring', 'mijia', 'scheduler', 'mcp'].includes(tg);
                                  return (
                                    <Badge
                                      key={tg}
                                      variant={isSensitive ? 'destructive' : 'outline'}
                                      className={`text-[10px] ${isSensitive ? 'bg-orange-100 text-orange-800 border-orange-200 hover:bg-orange-200' : ''}`}
                                    >
                                      {tg}
                                    </Badge>
                                  );
                                })
                              ) : (
                                <span className="text-xs text-muted-foreground">无</span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col gap-1">
                              <div className="flex items-center gap-1.5">
                                <Badge className="bg-indigo-600 text-white text-[10px] px-1.5 py-0">
                                  {item.quality_score ?? 80} 分
                                </Badge>
                                <span className="text-xs text-amber-600 font-medium">
                                  ★ {(item.star_rating ?? 4.0).toFixed(1)}
                                </span>
                              </div>
                              <span className="text-[11px] text-muted-foreground">
                                成功率 {Math.round((item.success_rate ?? 1.0) * 100)}% · {item.rating_count ?? 0} 人评
                              </span>
                            </div>
                          </TableCell>
                          <TableCell>
                            {item.status === 'published' ? (
                              <Badge className="bg-emerald-600">已发布</Badge>
                            ) : item.status === 'draft' ? (
                              <Badge variant="secondary">草稿</Badge>
                            ) : item.status === 'pending_review' ? (
                              <Badge className="bg-amber-500">待审核</Badge>
                            ) : item.status === 'rejected' ? (
                              <Badge className="bg-red-600">已驳回</Badge>
                            ) : (
                              <Badge variant="outline">已归档</Badge>
                            )}
                          </TableCell>
                          <TableCell className="text-sm font-medium">
                            {item.download_count}
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1.5">
                              {item.status === 'pending_review' && (
                                <>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs px-2 gap-1 text-emerald-700 border-emerald-200 bg-emerald-50/70 hover:bg-emerald-100 hover:text-emerald-800"
                                    onClick={() => handleReview(item, true)}
                                  >
                                    <CheckCircle2 className="w-3.5 h-3.5" />
                                    <span>通过</span>
                                  </Button>
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    className="h-7 text-xs px-2 gap-1 text-red-700 border-red-200 bg-red-50/70 hover:bg-red-100 hover:text-red-800"
                                    onClick={() => handleReview(item, false)}
                                  >
                                    <XCircle className="w-3.5 h-3.5" />
                                    <span>驳回</span>
                                  </Button>
                                </>
                              )}
                              <Button
                                variant="outline"
                                size="sm"
                                className="h-7 text-xs px-2 gap-1 text-blue-700 border-blue-200 bg-blue-50/70 hover:bg-blue-100 hover:text-blue-800"
                                onClick={() => handleOpenEdit(item)}
                              >
                                <Edit className="w-3.5 h-3.5" />
                                <span>编辑详情</span>
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => handleTogglePin(item)}
                                title={item.pinned ? '取消置顶' : '置顶（商店列表排最前）'}
                              >
                                {item.pinned ? (
                                  <PinOff className="w-4 h-4 text-amber-600" />
                                ) : (
                                  <Pin className="w-4 h-4 text-slate-500" />
                                )}
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2"
                                onClick={() => handleToggleStatus(item)}
                                title={item.status === 'published' ? '下架' : '发布'}
                              >
                                {item.status === 'published' ? (
                                  <XCircle className="w-4 h-4 text-orange-500" />
                                ) : (
                                  <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                                )}
                              </Button>
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <Button variant="ghost" size="sm" className="h-7 px-2" title="删除">
                                    <Trash2 className="w-4 h-4 text-red-600" />
                                  </Button>
                                </AlertDialogTrigger>
                                <AlertDialogContent>
                                  <AlertDialogHeader>
                                    <AlertDialogTitle>确认删除技能？</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      此操作将永久删除技能「{item.name}」({item.id})，已安装该技能的用户将无法获取后续更新。
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel>取消</AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDeleteSkill(item.id)}
                                      className="bg-red-600 hover:bg-red-700 text-white"
                                    >
                                      确认删除
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* 编辑 / 新建技能 Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{isCreate ? '新建技能' : `编辑技能 - ${editingSkill?.name}`}</DialogTitle>
              <DialogDescription>
                配置技能元数据、依赖工具组与完整 SKILL.md 提示词内容
              </DialogDescription>
            </DialogHeader>

            {editingSkill && (
              <div className="grid gap-4 py-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="skill_id">技能 ID (唯一标识，不可修改)</Label>
                    <Input
                      id="skill_id"
                      placeholder="如: health_advisor"
                      value={editingSkill.id || ''}
                      disabled={!isCreate}
                      onChange={(e) => setEditingSkill({ ...editingSkill, id: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="skill_name">技能显示名称</Label>
                    <Input
                      id="skill_name"
                      placeholder="如: 健康顾问助理"
                      value={editingSkill.name || ''}
                      onChange={(e) => setEditingSkill({ ...editingSkill, name: e.target.value })}
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="skill_desc">技能描述摘要</Label>
                  <Input
                    id="skill_desc"
                    placeholder="如: 结合智能戒指心率、血氧与睡眠数据提供专业健康建议"
                    value={editingSkill.description || ''}
                    onChange={(e) => setEditingSkill({ ...editingSkill, description: e.target.value })}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label>分类</Label>
                    <Select
                      value={editingSkill.category || 'efficiency'}
                      onValueChange={(val) => setEditingSkill({ ...editingSkill, category: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="health">健康生活 (health)</SelectItem>
                        <SelectItem value="efficiency">工作学习 (efficiency)</SelectItem>
                        <SelectItem value="roleplay">角色扮演 (roleplay)</SelectItem>
                        <SelectItem value="smart_home">智能家居 (smart_home)</SelectItem>
                        <SelectItem value="tool">实用工具 (tool)</SelectItem>
                        <SelectItem value="custom">自定义 (custom)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label>权限等级</Label>
                    <Select
                      value={editingSkill.access_level || 'free'}
                      onValueChange={(val: any) => setEditingSkill({ ...editingSkill, access_level: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free">全员免费 (free)</SelectItem>
                        <SelectItem value="vip">VIP 专享 (vip)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-1.5">
                    <Label>发布状态</Label>
                    <Select
                      value={editingSkill.status || 'published'}
                      onValueChange={(val: any) => setEditingSkill({ ...editingSkill, status: val })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="published">已发布 (published)</SelectItem>
                        <SelectItem value="draft">草稿 (draft)</SelectItem>
                        <SelectItem value="archived">已归档 (archived)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-1.5">
                    <Label htmlFor="skill_ver">语义化版本号</Label>
                    <Input
                      id="skill_ver"
                      placeholder="1.0.0"
                      value={editingSkill.version || '1.0.0'}
                      onChange={(e) => setEditingSkill({ ...editingSkill, version: e.target.value })}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="skill_ver_code">版本数值 (递增检测更新)</Label>
                    <Input
                      id="skill_ver_code"
                      type="number"
                      value={editingSkill.version_code || 1}
                      onChange={(e) => setEditingSkill({ ...editingSkill, version_code: Number.parseInt(e.target.value) || 1 })}
                    />
                  </div>
                  <div className="flex items-center space-x-2 pt-6">
                    <Switch
                      id="skill_always"
                      checked={!!editingSkill.always}
                      onCheckedChange={(checked) => setEditingSkill({ ...editingSkill, always: checked })}
                    />
                    <Label htmlFor="skill_always" className="text-sm font-normal">常驻系统 Prompt</Label>
                  </div>
                </div>

                {/* 依赖工具组 */}
                <div className="space-y-2 border rounded-lg p-3 bg-muted/30">
                  <div className="flex items-center justify-between">
                    <Label className="text-sm font-semibold flex items-center gap-1.5">
                      <ShieldCheck className="w-4 h-4 text-purple-600" />
                      依赖工具组 (ToolGroups 权限控制)
                    </Label>
                    <span className="text-xs text-muted-foreground">勾选后技能在 AI 对话中可调用对应工具</span>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2 pt-1">
                    {TOOL_GROUPS_OPTIONS.map((tg) => {
                      const checked = (editingSkill.tool_groups || []).includes(tg.id);
                      return (
                        <div
                          key={tg.id}
                          onClick={() => toggleToolGroup(tg.id)}
                          className={`flex items-center space-x-2 border rounded p-2 text-xs cursor-pointer transition-colors ${
                            checked
                              ? tg.sensitive
                                ? 'border-orange-500 bg-orange-50 text-orange-950 font-medium'
                                : 'border-purple-500 bg-purple-50 text-purple-950 font-medium'
                              : 'border-muted hover:bg-background'
                          }`}
                        >
                          <input type="checkbox" checked={checked} readOnly className="pointer-events-none" />
                          <div className="flex flex-col">
                            <span>{tg.label}</span>
                            {tg.sensitive && (
                              <span className="text-[10px] text-orange-600">需安装授权</span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* SKILL.md Markdown 正文 */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <Label htmlFor="skill_md" className="flex items-center gap-1.5">
                      <FileCode2 className="w-4 h-4" />
                      完整 SKILL.md 内容 (包含 frontmatter 与 Prompt 规范)
                    </Label>
                  </div>
                  <Textarea
                    id="skill_md"
                    rows={12}
                    className="font-mono text-xs leading-relaxed"
                    placeholder="---\nid: ...\nname: ...\n---\n\n# 技能提示词正文..."
                    value={editingSkill.content_markdown || ''}
                    onChange={(e) => setEditingSkill({ ...editingSkill, content_markdown: e.target.value })}
                  />
                </div>
              </div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>取消</Button>
              <Button onClick={handleSaveSkill}>保存技能</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* 导入 SKILL.md 弹窗 */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>从 SKILL.md 导入技能</DialogTitle>
              <DialogDescription>
                上传兼容 miniclawd / OpenClaw / ClawCode 标准的 .md 文件，系统将自动解析元数据与 Prompt 正文
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-3">
              <div className="space-y-1.5">
                <Label>选择 SKILL.md 文件</Label>
                <Input
                  type="file"
                  accept=".md,.txt"
                  onChange={(e) => setImportFile(e.target.files?.[0] || null)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>覆盖 ID (选填，留空则使用 frontmatter 中的 ID)</Label>
                <Input
                  placeholder="如: my_new_skill"
                  value={importOverrideId}
                  onChange={(e) => setImportOverrideId(e.target.value)}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setImportDialogOpen(false)}>取消</Button>
              <Button onClick={handleImportMarkdown} disabled={importing || !importFile}>
                {importing ? '正在解析导入...' : '开始导入'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </SidebarInset>
    </SidebarProvider>
  );
}
